import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/Button';
import SurveyQuestion from '../../../../components/SurveyQuestion/SurveyQuestion';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { clearGlobalVariablesForConversionTracking } from '../../../../lib/analytics/analyticsUtils';
import * as events from '../../../../lib/analytics/events';
import { logInternalError } from '../../../../lib/errors';
import ThankYouLocationState from '../../../../models/ThankYouLocationState';
import * as types from '../../../../types';
import { gqlTypes } from '../../../../types';
import styles from './ThankYou.module.scss';
import classNames from 'classnames';
import { ReactComponent as CheckmarkGreenCircle } from '../../../../assets/images/check-green-bg.svg';
import { ReactComponent as AppleAppStore } from '../../../../assets/images/apple_app_store.svg';
import { ReactComponent as GoogleAppStore } from '../../../../assets/images/google_app_store.svg';
import { useSeries3Context } from '../../../../contexts/Series3Context';
import { sessionActions } from '../../../../reducers/session';
import ReferralsSection from './ReferralsSection';
import { AffiliateTracker } from '../../../../components/AffiliateTracker';
import { useMutation } from '@apollo/client';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import { requiresMarketingConsent } from '../../../../lib/consent';
import { useLocale } from '../../../../contexts/LocaleContext';
import { setMarketingEmailsOptedInMutation } from '../../../../graphql-operations';

export default function ThankYou(): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const session = useSelector((state: types.AppState) => state.session);
  const showThankYouPageSurvey = useSelector((state: types.AppState) => state.config.siteConfig.showThankYouPageSurvey);
  const petId = useSelector((state: types.AppState) => state.series3UpgradeShop.upgradePetContext.petId);
  const inAppCancellationFlow = useSelector(
    (state: types.AppState) => state.series3UpgradeShop.upgradePetContext.inAppCancellationFlow,
  );
  const isWebView =
    useSelector((state: types.AppState) => state.series3UpgradeShop.upgradePetContext.isWebView) ?? false;

  const { cart, checkoutActions } = useContext(CheckoutContext);

  const location = useLocation<ThankYouLocationState>();
  const locationState = location?.state;
  const orderId = locationState?.orderId;
  const orderedCart = locationState?.orderedCart;
  const orderedCartPricing = locationState?.orderedCartPricing;
  const applePayUserSession = locationState?.applePayUserSession;
  const { isVerizon } = useSeries3Context();
  const verizonPostCheckoutMessage = useSelector(
    (state: types.AppState) => state.config.siteConfig.verizonPostCheckoutMessage,
  );

  const [hasTrackedPageLoad, setHasTrackedPageLoad] = useState(false);
  const [hasTrackedSurveySubmit, setHasTrackedSurveySubmit] = useState(false);
  const [marketingOptInChecked, setMarketingOptInChecked] = useState(false);
  const [marketingOptInSubmitted, setMarketingOptInSubmitted] = useState(false);
  const { country } = useLocale();

  // Only show marketing opt-in on the thank you page for Apple Pay / PayPal users who skipped
  // the review page (where the checkbox normally appears). Regular card checkout users already
  // had the opportunity to opt in on the review page.
  const showMarketingOptIn = !!applePayUserSession && requiresMarketingConsent(country);

  const [setMarketingOptIn] = useMutation<
    gqlTypes.ECOMMERCE_setMarketingEmailsOptedIn,
    gqlTypes.ECOMMERCE_setMarketingEmailsOptedInVariables
  >(setMarketingEmailsOptedInMutation);

  useEffect(() => {
    if (hasTrackedPageLoad) {
      return;
    }

    // Resetting the cart below triggers a re-render, so we need to make sure we don't do it multiple (infinity) times
    if (Object.keys(cart.cartItems).length === 0) {
      return;
    }

    // On mount: Track view, clear cart
    events.thankYou.viewed();
    inAppCancellationFlow && events.inAppCancellation.upgradeToS3CheckoutComplete({ petId });
    dispatch(checkoutActions.resetCart({ resetCoupon: true }));
    dispatch(checkoutActions.resetCheckout({}));
    setHasTrackedPageLoad(true);

    if (applePayUserSession && !session) {
      dispatch(sessionActions.createSession({ session: applePayUserSession }));
    }

    if (!orderId || !orderedCart) {
      logInternalError('ThankYou: Missing orderId or orderedCart in state');
    }
  }, [
    cart.cartItems,
    checkoutActions,
    dispatch,
    hasTrackedPageLoad,
    orderId,
    orderedCart,
    inAppCancellationFlow,
    petId,
    applePayUserSession,
    session,
  ]);

  useEffect(() => {
    // On unmount: clear tracking variables
    return () => {
      clearGlobalVariablesForConversionTracking();
    };
  }, []);

  const actionButton = useMemo(() => {
    if (isVerizon) {
      return (
        <Button onClick={() => window.postMessage(verizonPostCheckoutMessage, '*')}>{t('return_to_verizon')}</Button>
      );
    }

    if (isWebView) {
      return <Button onClick={() => window.open(`fi://closeappview`)}>{t('close_window')}</Button>;
    }

    return null;
  }, [isWebView, isVerizon, verizonPostCheckoutMessage, t]);

  const onSurveyQuestionSelect = useCallback(
    (answer: string): void => {
      if (hasTrackedSurveySubmit) {
        return;
      }
      events.thankYou.surveySubmit({
        selectedValue: answer,
        userId: session?.userId,
        email: session?.email,
      });

      setHasTrackedSurveySubmit(true);

      // Scroll back to the top of the page now that survey has been completed
      window.scrollTo(0, 0);
    },
    [session, hasTrackedSurveySubmit, setHasTrackedSurveySubmit],
  );

  return (
    <div className={classNames(styles.container, styles.containerV2)}>
      {orderId && orderedCart && (
        <AffiliateTracker orderId={orderId} cart={orderedCart} cartPricing={orderedCartPricing} />
      )}
      <div className={styles.main}>
        <CheckmarkGreenCircle />
        <h1>{t('your_order_is_confirmed')}</h1>
        <p>{t('well_send_confirmation_and_delivery_updates_to_email', { email: session?.email })}</p>
        {!isVerizon && (
          <div className={styles.appStoreButtonContainer}>
            <div>{t('download_the_fi_app_to_get_a_head_start')}</div>
            <div className={styles.appStoreButtons}>
              <a
                href="https://apps.apple.com/us/app/fi-gps-dog-tracker/id1438036784"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.appStoreButton}
              >
                <AppleAppStore />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.barkinglabs.fi"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.appStoreButton}
              >
                <GoogleAppStore />
              </a>
            </div>
          </div>
        )}
        {actionButton}
        <ReferralsSection />
        {showMarketingOptIn && !marketingOptInSubmitted && (
          <div className={styles.marketingOptIn}>
            <Checkbox
              checked={marketingOptInChecked}
              onChange={setMarketingOptInChecked}
              label={t('i_agree_to_receive_marketing_emails')}
              className={styles.marketingOptInCheckbox}
            />
            <Button
              onClick={() => {
                void setMarketingOptIn({ variables: { marketingEmailsOptedIn: marketingOptInChecked } }).catch((err) =>
                  logInternalError(err),
                );
                setMarketingOptInSubmitted(true);
              }}
            >
              {t('submit')}
            </Button>
          </div>
        )}
        {showMarketingOptIn && marketingOptInSubmitted && (
          <div className={styles.marketingOptInThankYou}>
            <p>{t('thank_you')}</p>
          </div>
        )}
        {!hasTrackedSurveySubmit && !isVerizon && showThankYouPageSurvey && (
          <SurveyQuestion
            className={styles.surveyQuestionWrapper}
            question={t('how_did_you_hear_about_us_question_mark')}
            onChoose={onSurveyQuestionSelect}
            options={[
              t('google'),
              t('friends_and_family'),
              t('facebook_instagram'),
              t('tiktok'),
              t('tv'),
              t('dog_park'),
              t('amazon'),
            ]}
          />
        )}
      </div>
    </div>
  );
}
